import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createItem, updateItem, uploadImage, listItemTypes, listManufacturers, listVendors, listWarehouses } from '../../lib/api';
import type { InventoryItem } from '../../types';

interface Props {
  item?: InventoryItem | null;
  onClose: () => void;
}

const UNITS = ['Each', 'Box', 'Pair', 'Set', 'Foot', 'Meter', 'Gallon', 'Pound', 'Roll'];

export default function EditDrawer({ item, onClose }: Props) {
  const isEdit = !!item;
  const qc = useQueryClient();

  const [form, setForm] = useState({
    name: '', part_number: '', model_name: '', part_description: '',
    item_type_id: '', manufacturer_id: '', vendor_id: '', warehouse_id: '',
    qty: '0', unit_of_measure: 'Each', cost_of_item: '0', notes: '',
    featured_image_key: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // Load reference data
  const { data: itemTypes = [] }    = useQuery({ queryKey: ['item-types'],    queryFn: listItemTypes });
  const { data: manufacturers = [] } = useQuery({ queryKey: ['manufacturers'], queryFn: listManufacturers });
  const { data: vendors = [] }       = useQuery({ queryKey: ['vendors'],       queryFn: listVendors });
  const { data: warehouses = [] }    = useQuery({ queryKey: ['warehouses'],    queryFn: listWarehouses });

  useEffect(() => {
    if (item) {
      setForm({
        name:              item.name,
        part_number:       item.part_number,
        model_name:        item.model_name ?? '',
        part_description:  item.part_description ?? '',
        item_type_id:      String(item.item_type_id ?? ''),
        manufacturer_id:   String(item.manufacturer_id ?? ''),
        vendor_id:         String(item.vendor_id ?? ''),
        warehouse_id:      String(item.warehouse_id ?? ''),
        qty:               String(item.qty),
        unit_of_measure:   item.unit_of_measure,
        cost_of_item:      String(item.cost_of_item),
        notes:             item.notes ?? '',
        featured_image_key: item.featured_image_key ?? '',
      });
      if (item.featured_image_key) {
        setImagePreview(`/api/images/${item.featured_image_key}`);
      }
    }
  }, [item]);

  const mutation = useMutation({
    mutationFn: async () => {
      let imageKey = form.featured_image_key;
      if (imageFile) {
        const uploaded = await uploadImage(imageFile);
        imageKey = uploaded.key;
      }
      const payload = {
        ...form,
        item_type_id:    form.item_type_id    ? parseInt(form.item_type_id)    : null,
        manufacturer_id: form.manufacturer_id ? parseInt(form.manufacturer_id) : null,
        vendor_id:       form.vendor_id       ? parseInt(form.vendor_id)       : null,
        warehouse_id:    form.warehouse_id    ? parseInt(form.warehouse_id)    : null,
        qty:             parseInt(form.qty) || 0,
        cost_of_item:    parseFloat(form.cost_of_item) || 0,
        featured_image_key: imageKey || null,
      };
      return isEdit ? updateItem(item!.id, payload) : createItem(payload);
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['item', saved.id] });
      onClose();
    },
  });

  function set(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit a Part' : 'Add a Part'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Select Part (edit only) */}
          {isEdit && (
            <div>
              <label className="label">Select Part</label>
              <input className="input bg-gray-50" value={form.name} disabled />
            </div>
          )}

          <div>
            <label className="label">Item Name (Common Name) <span className="text-red-500">*</span></label>
            <input className="input" value={form.name}
              onChange={(e) => set('name', e.target.value)} />
          </div>

          <div>
            <label className="label">Part Number <span className="text-red-500">*</span></label>
            <input className="input" value={form.part_number}
              onChange={(e) => set('part_number', e.target.value)} />
          </div>

          <div>
            <label className="label">Model Name</label>
            <input className="input" value={form.model_name}
              onChange={(e) => set('model_name', e.target.value)} />
          </div>

          <div>
            <label className="label">Part Description</label>
            <textarea className="input resize-none" rows={3}
              value={form.part_description}
              onChange={(e) => set('part_description', e.target.value)} />
          </div>

          <div>
            <label className="label">Item Type</label>
            <select className="input" value={form.item_type_id}
              onChange={(e) => set('item_type_id', e.target.value)}>
              <option value="">— Select —</option>
              {itemTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Manufacturer</label>
            <select className="input" value={form.manufacturer_id}
              onChange={(e) => set('manufacturer_id', e.target.value)}>
              <option value="">— Select —</option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Quantity <span className="text-red-500">*</span></label>
            <input type="number" className="input" min="0" value={form.qty}
              onChange={(e) => set('qty', e.target.value)} />
          </div>

          <div>
            <label className="label">Unit of Measure</label>
            <select className="input" value={form.unit_of_measure}
              onChange={(e) => set('unit_of_measure', e.target.value)}>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>

          <div>
            <label className="label">Warehouse Location</label>
            <select className="input" value={form.warehouse_id}
              onChange={(e) => set('warehouse_id', e.target.value)}>
              <option value="">— Select —</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Vendor</label>
            <select className="input" value={form.vendor_id}
              onChange={(e) => set('vendor_id', e.target.value)}>
              <option value="">— Select —</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Item Cost</label>
            <input type="number" className="input" min="0" step="0.01" value={form.cost_of_item}
              onChange={(e) => set('cost_of_item', e.target.value)} />
          </div>

          <div>
            <label className="label">Additional Notes</label>
            <textarea className="input resize-none" rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)} />
          </div>

          {/* Featured Image */}
          <div>
            <label className="label">Featured Image</label>
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded border mb-2" />
            )}
            <label className="btn-secondary cursor-pointer inline-block">
              Choose File
              <input type="file" className="sr-only" accept="image/*" onChange={handleFileChange} />
            </label>
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !form.name || !form.part_number}
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Part'}
          </button>
        </div>
      </div>
    </>
  );
}
